import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Edit, Package, TrendingUp, Activity, Building, Ruler, MapPin } from 'lucide-react';
import WarehouseService from '@/api/warehouses.service';
import { canEdit, canView } from '@/lib/permissions';

interface WarehouseLocation {
  publicId: string;
  code: string;
  name: string;
  description?: string;
  type: string;
  capacity?: number;
  dimensions?: string;
  status: 'active' | 'inactive';
  warehouseId: number;
  createdAt: string;
  updatedAt: string;
}

interface InventoryItem {
  id: string;
  itemName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  totalValue: number;
  lastUpdated: string;
}

interface MovementRecord {
  id: string;
  type: 'in' | 'out' | 'transfer';
  itemName: string;
  quantity: number;
  fromLocation?: string;
  toLocation?: string;
  timestamp: string;
  reference: string;
}

const WarehouseLocationDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  
  const [location, setLocation] = useState<WarehouseLocation | null>(null);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [movements, setMovements] = useState<MovementRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id) {
      fetchLocationData();
    }
  }, [id]);

  const fetchLocationData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // Fetch location details (placeholder implementation)
      // In a real implementation, we'd have a specific endpoint for single location
      const response = await WarehouseService.getWarehouseLocations('dummy');
      
      if (response.data?.success) {
        const locations = response.data.locations || [];
        const foundLocation = locations.find((loc: any) => loc.publicId === id);
        
        if (foundLocation) {
          setLocation(foundLocation);
        }
      }

      // Fetch inventory for this location
      try {
        const inventoryResponse = await WarehouseService.getInventoryStockByLocation(id);
        if (inventoryResponse.data?.success) {
          setInventory(inventoryResponse.data.inventory || []);
        }
      } catch (error) {
        console.log('Inventory not available:', error);
      }

      // Fetch movement history
      try {
        const movementsResponse = await WarehouseService.getInventoryMovementsByLocation(id);
        if (movementsResponse.data?.success) {
          setMovements(movementsResponse.data.movements || []);
        }
      } catch (error) {
        console.log('Movements not available:', error);
      }

    } catch (error) {
      console.error('Error fetching location data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch location details",
      });
      navigate('/warehouse/locations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' 
      ? <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
      : <Badge variant="secondary" className="bg-red-100 text-red-800">Inactive</Badge>;
  };

  const getTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      storage: 'Storage Area',
      receiving: 'Receiving Dock',
      shipping: 'Shipping Dock',
      picking: 'Picking Area',
      staging: 'Staging Area',
      quarantine: 'Quarantine Zone',
      returns: 'Returns Area',
      office: 'Office Space',
      maintenance: 'Maintenance Area',
      other: 'Other'
    };
    return typeMap[type] || type;
  };

  const formatCapacity = (capacity?: number) => {
    if (!capacity) return 'N/A';
    return capacity >= 1000 ? `${(capacity / 1000).toFixed(1)}K` : capacity.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getMovementTypeColor = (type: string) => {
    switch (type) {
      case 'in': return 'text-green-600';
      case 'out': return 'text-red-600';
      case 'transfer': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const getMovementTypeLabel = (type: string) => {
    switch (type) {
      case 'in': return 'Stock In';
      case 'out': return 'Stock Out';
      case 'transfer': return 'Transfer';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900">Location not found</h3>
        <p className="text-gray-500">The warehouse location you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/warehouse/locations')} className="mt-4">
          Back to Locations
        </Button>
      </div>
    );
  }

  const totalInventoryValue = inventory.reduce((sum, item) => sum + item.totalValue, 0);
  const totalItems = inventory.reduce((sum, item) => sum + item.quantity, 0);
  const utilizationPercentage = location.capacity ? Math.round((totalItems / location.capacity) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/warehouse/locations')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Locations
          </Button>
        </div>
        <div className="flex gap-2">
          {canEdit('Warehouse') && (
            <Button onClick={() => navigate(`/warehouse/locations/${location.publicId}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Location
            </Button>
          )}
        </div>
      </div>

      {/* Location Header Info */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{location.name}</CardTitle>
              <CardDescription className="text-lg">
                Code: {location.code}
              </CardDescription>
            </div>
            {getStatusBadge(location.status)}
          </div>
          {location.description && (
            <p className="text-muted-foreground mt-2">{location.description}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-start gap-2">
              <Building className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <p className="text-sm font-medium">Type</p>
                <p className="text-sm text-muted-foreground">{getTypeLabel(location.type)}</p>
              </div>
            </div>
            {location.capacity && (
              <div className="flex items-start gap-2">
                <Package className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm font-medium">Capacity</p>
                  <p className="text-sm text-muted-foreground">{formatCapacity(location.capacity)}</p>
                </div>
              </div>
            )}
            {location.dimensions && (
              <div className="flex items-start gap-2">
                <Ruler className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm font-medium">Dimensions</p>
                  <p className="text-sm text-muted-foreground">{location.dimensions}</p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <p className="text-sm font-medium">Utilization</p>
                <p className="text-sm text-muted-foreground">{utilizationPercentage}%</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalItems}</div>
            <p className="text-xs text-muted-foreground">Items in location</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inventory Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalInventoryValue)}</div>
            <p className="text-xs text-muted-foreground">Total value</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utilization</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{utilizationPercentage}%</div>
            <p className="text-xs text-muted-foreground">Capacity used</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">SKUs</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inventory.length}</div>
            <p className="text-xs text-muted-foreground">Unique items</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="inventory">Inventory ({inventory.length})</TabsTrigger>
          <TabsTrigger value="movements">Movements ({movements.length})</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Location Summary</CardTitle>
              <CardDescription>Key information about this warehouse location</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Physical Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type:</span>
                      <span>{getTypeLabel(location.type)}</span>
                    </div>
                    {location.capacity && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Capacity:</span>
                        <span>{formatCapacity(location.capacity)}</span>
                      </div>
                    )}
                    {location.dimensions && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Dimensions:</span>
                        <span>{location.dimensions}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <span>{getStatusBadge(location.status)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Current Stock</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Items:</span>
                      <span>{totalItems}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Unique SKUs:</span>
                      <span>{inventory.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Value:</span>
                      <span>{formatCurrency(totalInventoryValue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Utilization:</span>
                      <span>{utilizationPercentage}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inventory" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Current Inventory</CardTitle>
              <CardDescription>Items currently stored in this location</CardDescription>
            </CardHeader>
            <CardContent>
              {inventory.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No inventory found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This location currently has no items in stock.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Item Name</th>
                        <th className="text-left py-2">SKU</th>
                        <th className="text-right py-2">Quantity</th>
                        <th className="text-right py-2">Unit Price</th>
                        <th className="text-right py-2">Total Value</th>
                        <th className="text-left py-2">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventory.map((item) => (
                        <tr key={item.id} className="border-b">
                          <td className="py-2">{item.itemName}</td>
                          <td className="py-2 text-muted-foreground">{item.sku}</td>
                          <td className="py-2 text-right">{item.quantity}</td>
                          <td className="py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                          <td className="py-2 text-right font-medium">{formatCurrency(item.totalValue)}</td>
                          <td className="py-2 text-muted-foreground">
                            {new Date(item.lastUpdated).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Movement History</CardTitle>
              <CardDescription>Recent inventory movements for this location</CardDescription>
            </CardHeader>
            <CardContent>
              {movements.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No movements found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No inventory movements have been recorded for this location.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {movements.map((movement) => (
                    <div key={movement.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`font-medium ${getMovementTypeColor(movement.type)}`}>
                          {getMovementTypeLabel(movement.type)}
                        </div>
                        <div>
                          <p className="font-medium">{movement.itemName}</p>
                          <p className="text-sm text-muted-foreground">
                            Quantity: {movement.quantity}
                            {movement.fromLocation && ` • From: ${movement.fromLocation}`}
                            {movement.toLocation && ` • To: ${movement.toLocation}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          {new Date(movement.timestamp).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Ref: {movement.reference}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Location Analytics</CardTitle>
              <CardDescription>Performance metrics and insights</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Analytics Coming Soon</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Detailed analytics and performance metrics will be available here.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WarehouseLocationDetail; 